/* eslint-disable react-refresh/only-export-components */
import { createContext, useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react'
import { confirmClientWorkExit, resetClientWorkExit } from '../utils/clientWorkNavigation'
import { useAuth } from '../hooks/useAuth'
import { productApi } from '../services/api'
import { hasProductAction } from '../config/studioProducts'
import { ROLES, canRoleAccessResource } from '../constants/roles'

export const OrganizationContext = createContext(null)

export function useOrganization() {
  const context = useContext(OrganizationContext)
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider')
  }
  return context
}

export function OrganizationProvider({ children }) {
  const { organizations, isAuthenticated, loading: authLoading, refreshProfile } = useAuth()
  const [currentOrg, setCurrentOrg] = useState(null)
  const [loading, setLoading] = useState(true)
  const [productsLoading, setProductsLoading] = useState(false)
  const [productsReadOrg, setProductsReadOrg] = useState(null)
  const switchRequestVersion = useRef(0)

  useEffect(() => {
    // Wait for auth to finish loading before processing organizations
    if (authLoading) {
      return
    }
    
    if (isAuthenticated && organizations.length > 0) {
      const storedOrg = localStorage.getItem('current_org')
      if (storedOrg) {
        try {
          const parsed = JSON.parse(storedOrg)
          // Find the full organization object with all fields
          const validOrg = organizations.find(o => o.id === parsed.id)
          if (validOrg) {
            // Use the fresh org data from the API, not the stored one
            setCurrentOrg(validOrg)
            // Update localStorage with fresh data
            localStorage.setItem('current_org', JSON.stringify(validOrg))
          } else {
            // Stored org no longer valid, use first available
            setCurrentOrg(organizations[0])
            localStorage.setItem('current_org', JSON.stringify(organizations[0]))
          }
        } catch {
          // Invalid stored data, use first org
          setCurrentOrg(organizations[0])
          localStorage.setItem('current_org', JSON.stringify(organizations[0]))
        }
      } else {
        // No stored org, use first available
        setCurrentOrg(organizations[0])
        localStorage.setItem('current_org', JSON.stringify(organizations[0]))
      }
    } else if (!isAuthenticated) {
      setCurrentOrg(null)
      localStorage.removeItem('current_org')
    }
    
    setLoading(false)
  }, [organizations, isAuthenticated, authLoading])

  const refreshProductGrants = useCallback(async (orgSlug = currentOrg?.slug) => {
    if (!orgSlug) return null

    setProductsLoading(true)
    try {
      const response = await productApi.list(orgSlug)
      const products = response?.products || []
      setCurrentOrg((current) => {
        if (!current || current.slug !== orgSlug) return current

        const nextOrg = { ...current, products }
        localStorage.setItem('current_org', JSON.stringify(nextOrg))
        return nextOrg
      })
      return products
    } catch (error) {
      console.warn('Failed to refresh product grants:', error)
      return null
    } finally {
      setProductsLoading(false)
    }
  }, [currentOrg?.slug])

  // Session payloads (sign-in, refresh, profile) carry each organization's
  // product grants, read as /products reads them. Ask again only when the
  // session could not read them (products: null).
  const sessionHasProducts = Array.isArray(currentOrg?.products)

  useEffect(() => {
    if (!isAuthenticated || !currentOrg?.slug || sessionHasProducts) return

    let mounted = true
    const loadProducts = async () => {
      setProductsLoading(true)
      try {
        const response = await productApi.list(currentOrg.slug)
        if (!mounted) return

        const products = response?.products || []
        setCurrentOrg((current) => {
          if (!current || current.slug !== currentOrg.slug) return current

          const nextOrg = { ...current, products }
          localStorage.setItem('current_org', JSON.stringify(nextOrg))
          return nextOrg
        })
      } catch (error) {
        if (mounted) {
          console.warn('Failed to refresh product grants:', error)
        }
      } finally {
        if (mounted) {
          setProductsReadOrg(currentOrg)
          setProductsLoading(false)
        }
      }
    }

    loadProducts()
    return () => {
      mounted = false
      // A superseded read never clears the flag itself, and the next
      // organization may not read at all.
      setProductsLoading(false)
    }
  }, [currentOrg, isAuthenticated, sessionHasProducts])

  // Resolves to the organization as the refreshed profile describes it (role
  // and product grants included), or false when the switch didn't happen.
  const switchOrganization = useCallback(async (orgSlug) => {
    if (!confirmClientWorkExit()) return false
    const failedSwitch = () => { resetClientWorkExit(); return false }
    const requestVersion = switchRequestVersion.current + 1
    switchRequestVersion.current = requestVersion

    let availableOrganizations
    try {
      const profile = await refreshProfile()
      if (switchRequestVersion.current !== requestVersion) return failedSwitch()
      availableOrganizations = profile?.organizations || []
    } catch {
      return failedSwitch()
    }

    const org = availableOrganizations.find((item) => item.slug === orgSlug)
    if (!org) return failedSwitch()

    const completeOrg = {
      id: org.id,
      slug: org.slug,
      name: org.name || org.slug,
      role: org.role || ROLES.VIEWER,
      ...org,
    }
    setCurrentOrg(completeOrg)
    localStorage.setItem('current_org', JSON.stringify(completeOrg))
    return completeOrg
  }, [refreshProfile])

  const hasPermission = useCallback((permission) => {
    if (!currentOrg) return false
    
    const rolePermissions = {
      [ROLES.SUPER_ADMIN]: ['*'],
      [ROLES.INTERNAL_ORG_ADMIN]: [
        'manage_resources',
        'manage_posts',
        'manage_users',
        'view_metrics',
        'manage_forms'
      ],
      [ROLES.EXTERNAL_ORG_ADMIN]: [
        'manage_posts',
        'manage_users',
        'view_metrics'
      ],
      [ROLES.EDITOR]: [
        'create_posts',
        'edit_own_posts',
        'view_posts',
        'view_metrics'
      ],
      [ROLES.VIEWER]: [
        'view_posts',
        'view_metrics'
      ]
    }
    
    const permissions = rolePermissions[currentOrg.role] || []
    return permissions.includes('*') || permissions.includes(permission)
  }, [currentOrg])

  const canAccessResource = useCallback((resourceType) => {
    if (!currentOrg) return false
    return canRoleAccessResource(currentOrg.role, resourceType)
  }, [currentOrg])

  const hasProductAccess = useCallback((productKey, action = 'view') => {
    return hasProductAction(currentOrg, productKey, action)
  }, [currentOrg])

  // Effects have not selected an organization or started its fallback read
  // on the first authenticated render. Keep consumers waiting through that
  // gap, but let a failed read settle instead of leaving a permanent spinner.
  const workspaceLoading = loading || authLoading || (isAuthenticated && organizations.length > 0 && !currentOrg)
  const grantsLoading = productsLoading || Boolean(
    isAuthenticated && currentOrg?.slug && !sessionHasProducts && productsReadOrg !== currentOrg,
  )

  const value = useMemo(() => ({
    currentOrg,
    organizations,
    loading: workspaceLoading,
    productsLoading: grantsLoading,
    switchOrganization,
    refreshProductGrants,
    hasPermission,
    canAccessResource,
    hasProductAccess,
    isOrgAdmin: currentOrg?.role?.includes('admin') || false,
    isSuperAdmin: currentOrg?.role === ROLES.SUPER_ADMIN,
  }), [
    currentOrg,
    organizations,
    workspaceLoading,
    grantsLoading,
    switchOrganization,
    refreshProductGrants,
    hasPermission,
    canAccessResource,
    hasProductAccess,
  ])

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  )
}
